import { createStyles, withStyles } from '@material-ui/core'
import {
  useProcessEvent,
  Alert,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  Spinner
} from '@paintscout/ui'
import type { StyleClasses, DialogProps } from '@paintscout/ui'
import { QuoteContext } from '@paintscout/react-quote'
import { useSnackbar } from 'notistack'
import type { EventType } from 'paintscout'
import React, { useContext, useEffect, useState } from 'react'

export interface ProcessEventDialogProps extends DialogProps {
  classes?: DialogProps['classes'] & StyleClasses<typeof styles>
  onClose?: () => void
  onConfirm?: (result: any) => void
  provider: string
  title: string
  async?: boolean
  eventTypeOverride?: EventType
  params?: any
}

const styles = () =>
  createStyles({
    root: {
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      width: '100%'
    },
    input: { width: '100%', height: '100%' },
    content: { maxHeight: '100%' }
  })

function ProcessEventDialog({
  classes,
  onClose,
  onConfirm,
  provider,
  params,
  title,
  async,
  eventTypeOverride = 'interface' as EventType,
  ...otherProps
}: ProcessEventDialogProps) {
  const { enqueueSnackbar } = useSnackbar()
  const { updateQuote } = useContext(QuoteContext)
  const { processEvent } = useProcessEvent()

  const [loading, setLoading] = useState(true)

  const [error, setError] = useState('')
  useEffect(() => {
    async function process(type: EventType = eventTypeOverride, innerAsync: boolean = async) {
      const res = await processEvent({
        type,
        async: innerAsync,
        provider,
        params
      })
      if (res === null) {
        enqueueSnackbar('Network connection lost, please try again later.', { variant: 'error' })
        onClose()
      } else if (res.error) {
        const errorMessage =
          typeof res.error === 'string' ? res.error : (res.error as any).message ?? 'An unknown error occurred.'
        setError(errorMessage)
        enqueueSnackbar(`${title} unsuccessful${errorMessage.length ? ` - ${errorMessage}` : ''}`, { variant: 'error' })
      } else if (res.result) {
        // We can retrigger more async events after a sync event if we give it a trigger
        if (res.result.trigger) {
          process(res.result.trigger as EventType, true)
        }
        if (params.syncOnComplete && res?.result?.result?.type === 'quote') {
          updateQuote({ quote: res.result.result, autosave: false, blockDirty: true })
        }
        if (onConfirm) {
          onConfirm(res.result)
        }
        enqueueSnackbar(`${title} successful`, { variant: 'success' })
        onClose()
      }

      setLoading(false)
      onClose()
    }
    process()
  }, [])

  return (
    <Dialog {...otherProps}>
      <DialogTitle>{title}</DialogTitle>
      <DialogContent className={classes.content}>
        <Grid container spacing={2} alignItems={'center'}>
          {loading && (
            <Grid item xs={12}>
              <Spinner />
            </Grid>
          )}
          {error && (
            <Grid item xs={12}>
              <Alert severity="error" title={`There was an error with your integration.`} content={error} />
            </Grid>
          )}
        </Grid>
      </DialogContent>
      <DialogActions>
        <Grid container justifyContent={error ? 'flex-end' : 'flex-start'}>
          <Grid item>
            {loading && (
              <Button onClick={onClose} variant={'text'}>
                Cancel
              </Button>
            )}
            {!!error && (
              <Button onClick={onClose} variant="contained">
                OK
              </Button>
            )}
          </Grid>
        </Grid>
      </DialogActions>
    </Dialog>
  )
}

export default withStyles(styles)(ProcessEventDialog)
